import NotificationPerTime from 'component/navigation/NotificationPerTime';
import ScrollToTop from 'containers/scroll_to_top';
import { AppVersionProvider } from 'features/appVersion/appVersionProvider';
import { UserDetailProvider } from 'features/users/UserProvider';
import { getToken, onMessageListener } from './firebase';
import { useCallback, useEffect, useState, useRef } from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { MainRouting } from 'routes';
import './App.scss';
import { LogConfig } from 'features/configuration/LogConfig';
import GlobalLoadingSpinner from 'component/loading_spinner/global_loading_spinner';
import { PRODUCT_TYPE, TRACK_EVENT, trackEvent } from './utils/analytics';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from './redux/store';
import { useHistory, useLocation } from 'react-router-dom';
import {
    setProductDetails,
    setSearchGender,
    setSelectedQuestion,
    setSourceUrl,
    setWidgetOpenState,
    setIsPdp,
} from './redux/widget/widget.slice';
import { AuthWidget } from 'features/auth/auth_widget';
import { PATH } from 'routes/constant';
import { AppInternetStatusProvider } from 'features/appInternetStatus/appInternetStatusProvider';
import { WIDGET_CONSTANTS } from 'constants/iframe-events';
import { BottomNavigationProvider } from 'routes/BottomNavigationProvider';
import { devModeAllowedKeys, STORE_DEV_MODE_ATTR, STRINGS, TAFFI_DEV_MODE } from './constants';
import { checkIsPersonalizedStyle, getProductTrackingData, getItemTrackingData } from './utils/trackUtils';
import { SESSION_STORAGE_KEY } from './utils/constant';
import isEmpty from 'lodash/isEmpty';
import { useLazyQuery, useMutation } from '@apollo/client';
import { GET_RECOMMENDATIONS, GET_MY_STYLES } from 'graph_ql/styles/queries';
import utils from './utils/utils';
import { AlgoliaInput, AlgoliaResponse } from '@gql/types/types';
import { getItem_getItem_data } from './__generated__/getItem';
import { GET_ITEM_BY_ID, GET_ITEMS_BY_ID } from 'graph_ql/item/queries';
import styleItemUtils from './utils/style_item';
import { pick } from 'lodash';
import { Gender, StyleStatus } from './__generated__/globalTypes';
import { ProductTargetGenderEnum } from './containers/my_sessions/types';
import { updateIsLinkClicked } from './redux/chat/chat.slice';
import get from 'lodash/get';
import './i18n';
import config from 'config';

const App = () => {
    const location = useLocation();
    const dispatch = useAppDispatch();
    const userDetail = useSelector((state: RootState) => state.userDetail.me);
    const searchGender = useSelector((state: RootState) => state.widget?.searchGender);
    const isInternalLinkClicked = useSelector((state: RootState) => state.chat?.isLinkClicked);
    const store = useSelector((state: RootState) => state.widget?.store);
    const history = useHistory();
    const localUser = utils.getLocalUser();

    const [, setTokenFound] = useState(false);
    getToken(setTokenFound);
    onMessageListener()
        .then((payload) => {
            console.log('Message FCM Foreground ===========================>', {
                title: payload.notification.title,
                body: payload.notification.body,
            });
        })
        .catch((err) => 0 && console.error('FCM failed: ', err));

    const [getItem] = useLazyQuery<AlgoliaResponse<getItem_getItem_data, 'algolia_get_item_by_id'>, AlgoliaInput>(
        GET_ITEMS_BY_ID,
    );

    const [getRecommendations] = useMutation(GET_RECOMMENDATIONS, {
        context: {
            allowAnonymousAuth: true,
        },
        onCompleted: async (response) => {
            const suggestions = response?.getRecommendations;
            if (!suggestions) {
                return;
            }
            const currentSearchGender = getUserGender();
            if (!searchGender) {
                dispatch(setSearchGender(currentSearchGender));
            }
            if (suggestions.recommendations.length) {
                const promises: any = [];
                for (const style of suggestions.recommendations) {
                    const promise = new Promise(async (resolve) => {
                        try {
                            const itemCondition = style.items.reduce((condition: string, i: any) => {
                                condition += (condition === '' ? '' : ' OR ') + `objectID:${i.id}`;
                                return condition;
                            }, '');
                            const itemsResponse = await getItem({
                                variables: {
                                    filters: itemCondition,
                                },
                            });
                            const items = itemsResponse.data?.algolia_get_item_by_id.hits.map((data: any) => {
                                return {
                                    id: data.id,
                                    data,
                                };
                            });
                            resolve({
                                ...style,
                                items: items ? styleItemUtils.mergeItemArrayObjects(style.items, items) : style.items,
                            });
                        } catch (e: any) {
                            console.log('Error merge algolia items for style ', e);
                            resolve(style);
                        }
                    });
                    promises.push(promise);
                }
                console.time('getAllItemsForSearchRecommendations');
                try {
                    suggestions.recommendations = await Promise.all(promises);
                } catch (e) {
                    console.log('Error merge algolia items for style ', e);
                }
                console.timeEnd('getAllItemsForSearchRecommendations');
            }
            window.parent.postMessage(
                {
                    name: WIDGET_CONSTANTS.SEARCH_WIDGET_RECOMMENDATIONS,
                    suggestions,
                    searchGender: searchGender ?? currentSearchGender,
                },
                '*',
            );
        },
        onError() {
            console.log('get style contain item error');
        },
    });

    const [getSingleRecommendation] = useMutation(GET_RECOMMENDATIONS, {
        context: {
            allowAnonymousAuth: true,
        },
    });

    const getUserGender = useCallback(() => {
        if (searchGender) {
            return searchGender;
        }

        if (userDetail?.gender) {
            return userDetail.gender;
        }
        //get gender from local user
        const localUser = utils.getLocalUser();
        if (localUser && localUser.gender) {
            return localUser.gender;
        }
        if (store && store.productTargetGender?.length) {
            if (store.productTargetGender.length === 1) {
                return utils.correctGender(store.productTargetGender[0]);
            }
            if (
                store.productTargetGender.includes(ProductTargetGenderEnum.Man) &&
                store.productTargetGender.includes(ProductTargetGenderEnum.Woman)
            ) {
                return Gender.female;
            }
        }
        return null;
    }, [searchGender, store, userDetail?.gender]);

    const [getItemDetail] = useLazyQuery<AlgoliaResponse<getItem_getItem_data, 'algolia_get_item_by_id'>, AlgoliaInput>(
        GET_ITEM_BY_ID,
    );

    const [getMyStyles] = useLazyQuery(GET_MY_STYLES);

    // Ref to store cached styles with key as origin + productUrl
    const stylesCache = useRef(new Map());

    // Ref to store product view tracking data for future use
    const hangerIconDisplayedDataRef = useRef<any[]>([]);

    const getItemsByUrls = useCallback(
        async (productUrls: string[], storeId: null | undefined | string) => {
            const promises = productUrls.map(async (productUrl) => {
                const result = await getItemDetail({
                    variables: {
                        query: productUrl,
                        filters: storeId ? `storeId:${storeId} AND status:available` : 'status:available',
                    },
                });
                const item = get(result, ['data', 'algolia_get_item_by_id', 'hits', 0], null);
                return {
                    productUrl,
                    exists: !!item,
                };
            });
            const results = await Promise.all(promises);
            // return only the product urls that exist in Algolia
            return results.filter((result) => result.exists).map((result) => result.productUrl);
        },
        [getItemDetail],
    );

    const getStylesByUrls = useCallback(
        async (productUrls: string[], sourceUrl: string = '') => {
            const urlObject = new URL(sourceUrl);

            let index = config.algolia.algolia_prefix + 'styles';
            // prepare filter condition based on productUrls
            let filters = 'status:published';
            const orConditions = productUrls.map((productUrl) => {
                return `(allItemUrls:"${urlObject.origin}${productUrl}" OR items.allItemUrls:"${urlObject.origin}${productUrl}")`;
            });
            filters += ' AND (' + orConditions.join(' OR ') + ')';
            const result = await getMyStyles({
                variables: {
                    index,
                    query: '',
                    filters: filters,
                    hit: 200,
                    page: 0,
                },
            });
            const hits = get(result, ['data', 'locationsInRadius', 'hits'], []);
            const results = productUrls.map((productUrl) => {
                const productUrlSegments = productUrl.split('/');
                const lastSegment = productUrlSegments[productUrlSegments.length - 1];
                // Apply validStyles logic similar to the model.ts file
                const validStyles = hits.filter((style: any) => {
                    let valid = false;
                    for (const item of style.items || []) {
                        const itemUrlObject = new URL(item.data.url);
                        if (urlObject.origin !== itemUrlObject.origin) {
                            if (
                                store?.allowedItemCategoriesFromOtherStores?.length &&
                                store?.allowedItemCategoriesFromOtherStores?.includes(item?.data?.metadata?.category)
                            ) {
                                continue;
                            }
                            return false;
                        }
                        const itemPath = itemUrlObject.pathname;
                        const itemPathSegments = itemPath.split('/');
                        const itemLastSegment = itemPathSegments[itemPathSegments.length - 1];
                        if (lastSegment === itemLastSegment) {
                            valid = true;
                        }
                        if (item?.data?.alternativeUrls?.length) {
                            // check if lastSegment match any of the alternativeUrls
                            for (const alternativeUrl of item?.data?.alternativeUrls) {
                                const alternativeUrlObject = new URL(alternativeUrl);
                                const alternativePath = alternativeUrlObject.pathname;
                                const alternativePathSegments = alternativePath.split('/');
                                const alternativeLastSegment =
                                    alternativePathSegments[alternativePathSegments.length - 1];
                                if (lastSegment === alternativeLastSegment) {
                                    valid = true;
                                    break;
                                }
                            }
                        }
                    }
                    return valid;
                });
                // Store validStyles in cache if they exist
                if (validStyles.length > 0) {
                    const cacheKey = `${urlObject.origin}${productUrl}`;
                    stylesCache.current.set(cacheKey, validStyles);
                }

                return {
                    productUrl,
                    exists: validStyles.length > 0,
                };
            });
            // return only the product urls that have valid styles in Algolia
            return results.filter((result) => result.exists).map((result) => result.productUrl);
        },
        [getMyStyles, store?.allowedItemCategoriesFromOtherStores],
    );

    // listen to widget events
    const handleMessage = useCallback(
        (event: MessageEvent<any>) => {
            if (event?.data?.name === TRACK_EVENT.STYLE_WIDGET_AMIRA_CLICKED) {
                const primaryProduct = event.data.primaryProduct;
                const recommendationSource = event.data.recommendationSource;
                trackEvent(event.data.name, {
                    sourceUrl: event.data.sourceUrl,
                    queryParams: event.data.queryParams,
                    userId: userDetail?.id,
                    userEmail: userDetail?.email,
                    ctaLinkText: event.data.ctaLinkText,
                    messageText: event.data.messageText,
                    primaryStoreProductId: primaryProduct?.data?.storeProductId,
                    primaryTaffiProductId: primaryProduct?.data?.taffiId,
                    primaryProductId: primaryProduct?.id,
                    recommendationSource,
                });
                history.push(PATH.STYLE_ASSISTANT);
            }
            if (event?.data?.name === TRACK_EVENT.DRESS_IT_RIGHT_VIEWED) {
                const primaryProduct = event.data.primaryProduct;
                trackEvent(event.data.name, {
                    sourceUrl: event.data.sourceUrl,
                    queryParams: event.data.queryParams,
                    userId: userDetail?.id,
                    userEmail: userDetail?.email,
                    primaryStoreProductId: primaryProduct?.data?.storeProductId,
                    primaryTaffiProductId: primaryProduct?.data?.taffiId,
                    primaryProductId: primaryProduct?.id,
                });
            }
            if (event?.data?.name === TRACK_EVENT.PRODUCT_VIEW) {
                const primaryProduct = event.data.primaryProduct;
                let recommendationSource = STRINGS.STATIC_PRODUCT_DETAIL_PAGE;
                if (event?.data?.isFromPLP) {
                    recommendationSource = STRINGS.STATIC_PRODUCT_LISTING_PAGE;
                }
                if (event?.data?.isFromSearch) {
                    recommendationSource = STRINGS.STATIC_PRODUCT_SEARCH_PAGE;
                }
                const styleData = event.data.styleData;
                const productViewTrackingData = {
                    ...getProductTrackingData(userDetail, {
                        ...event.data.styleData,
                        collection: styleData?.collection ?? styleData?.collectionName ?? 'styles',
                    }),
                    styleLayoutVersion: event?.data?.styleLayoutVersion,
                    personalized: checkIsPersonalizedStyle(event.data.styleData, userDetail),
                    recommendationSource,
                    sourceUrl: event.data.sourceUrl,
                    queryParams: event.data.queryParams,
                    primaryStoreProductId: primaryProduct?.data?.storeProductId,
                    primaryTaffiProductId: primaryProduct?.data?.taffiId,
                    primaryProductId: primaryProduct?.id,
                };
                trackEvent(event.data.name, productViewTrackingData);
            }
            if (event?.data?.name === TRACK_EVENT.STYLE_IT_RIGHT_STYLE_CLICKED) {
                const primaryProduct = event.data.primaryProduct;
                const productUrlObject = new URL(event.data.sourceUrl);
                const productUrl = event?.data?.isMultiStylePage
                    ? event.data.sourceUrl
                    : productUrlObject.origin + productUrlObject.pathname;
                const styleTaffiId = event.data.styleTaffiId;
                trackEvent(event.data.name, {
                    sourceUrl: event.data.sourceUrl,
                    queryParams: event.data.queryParams,
                    userId: userDetail?.id,
                    userEmail: userDetail?.email,
                    styleId: event.data.styleId,
                    styleTaffiId: event.data.styleTaffiId,
                    primaryStoreProductId: primaryProduct?.data?.storeProductId,
                    primaryTaffiProductId: primaryProduct?.data?.taffiId,
                    primaryProductId: primaryProduct?.id,
                });
                history.push(`${PATH.STYLE_IT_RIGHT}`, {
                    title: STRINGS.STYLE_IT_RIGHT,
                    taffiId: styleTaffiId,
                    sourceProductURL: productUrl,
                });
            }
            if (event?.data?.name === TRACK_EVENT.TAFFI_WIDGET_LISTING_STYLE_CLICKED) {
                const primaryProduct = event.data.primaryProduct;
                trackEvent(event.data.name, {
                    sourceUrl: event.data.sourceUrl,
                    productUrl: event.data.productUrl,
                    queryParams: event.data.queryParams,
                    userId: userDetail?.id,
                    userEmail: userDetail?.email,
                    styleId: event.data.styleId,
                    styleTaffiId: event.data.styleTaffiId,
                    primaryStoreProductId: primaryProduct?.storeProductId,
                    primaryTaffiProductId: primaryProduct?.taffiId,
                    primaryProductId: primaryProduct?.id,
                });
            }
            if (
                event?.data?.name === TRACK_EVENT.PLP_PLACEHOLDER_PRODUCT_CLICKED ||
                event?.data?.name === TRACK_EVENT.PLP_PLACEHOLDER_PRODUCT_VIEW
            ) {
                trackEvent(event.data.name, {
                    sourceUrl: event.data.sourceUrl,
                    queryParams: event.data.queryParams,
                    userId: userDetail?.id,
                    userEmail: userDetail?.email,
                    productUrl: event.data.productUrl,
                    productTitle: event.data.productTitle,
                    recommendationSource: STRINGS.STATIC_PRODUCT_LISTING_PAGE,
                });
            }
            if (event?.data?.name === TRACK_EVENT.TAFFI_WIDGET_POPUP_QUESTION_CLICKED && event?.data?.question) {
                trackEvent(event.data.name, {
                    sourceUrl: event.data.sourceUrl,
                    queryParams: event.data.queryParams,
                    customerId: userDetail?.id,
                    customerEmail: userDetail?.email,
                    questionText: event?.data?.question,
                });
                dispatch(setSelectedQuestion(event?.data?.question));
                localStorage.setItem(WIDGET_CONSTANTS.STYLE_ASSISTANT_SOURCE, WIDGET_CONSTANTS.WIDGET_QUESTION_POPUP);
                history.push(PATH.STYLE_ASSISTANT);
            }
            if (event.data.name === TRACK_EVENT.WIDGET_OPENED || event.data.name === TRACK_EVENT.WIDGET_CLOSED) {
                trackEvent(event.data.name, {
                    sourceUrl: event.data.sourceUrl,
                    userId: userDetail?.id,
                    userEmail: userDetail?.email,
                    userType: userDetail?.userType,
                    appUrl: location.pathname,
                    ...(event?.data?.name === TRACK_EVENT.WIDGET_OPENED && {
                        isAutomated: Boolean(event.data?.isAutomated),
                    }),
                });
                sessionStorage.setItem(
                    WIDGET_CONSTANTS.TAFFI_WIDGET_OPENED,
                    JSON.stringify(event.data.name === TRACK_EVENT.WIDGET_OPENED),
                );
                dispatch(setWidgetOpenState(event.data.name === TRACK_EVENT.WIDGET_OPENED));
            }

            if (
                [
                    TRACK_EVENT.TAFFI_WIDGET_LOADED,
                    // TRACK_EVENT.TAFFI_WIDGET_POPUP_DISPLAYED,
                    // TRACK_EVENT.TAFFI_WIDGET_POPUP_USER_CLOSED,
                    TRACK_EVENT.TAFFI_WIDGET_NOT_DISPLAYED,
                ].includes(event.data.name)
            ) {
                if (event.data.name === TRACK_EVENT.TAFFI_WIDGET_LOADED) {
                    console.log('%cDISPATCH setSourceUrl', 'color: red');
                    console.log(event.data.sourceUrl);
                    const queryParams = event.data.queryParams;
                    if (queryParams && queryParams.hasOwnProperty(TAFFI_DEV_MODE)) {
                        if (queryParams[TAFFI_DEV_MODE] === 'true') {
                            // save attributes that need to be overridden
                            let storeDevAttributes: any = {};
                            for (const key in queryParams) {
                                if (devModeAllowedKeys.includes(key)) {
                                    storeDevAttributes[key] = queryParams[key] === 'true';
                                }
                            }
                            if (!isEmpty(storeDevAttributes)) {
                                sessionStorage.setItem(STORE_DEV_MODE_ATTR, JSON.stringify(storeDevAttributes));
                            }
                        } else {
                            // clear attributes and reset store information to the original
                            sessionStorage.removeItem(STORE_DEV_MODE_ATTR);
                            sessionStorage.removeItem(SESSION_STORAGE_KEY.TAFFI_STORE_DETAILS);
                        }
                    }
                    dispatch(setSourceUrl(event.data.sourceUrl));
                    dispatch(setIsPdp(event.data.isPdp));
                    stylesCache.current.clear();
                    const taffiPagePath = event.data.taffiPagePath;
                    const taffiContextId = event.data.taffiContextId;
                    const taffiRequestId = event.data.taffiRequestId;
                    if (event.data.taffiPagePath) {
                        history.push(taffiPagePath);
                    } else if (taffiContextId) {
                        if (taffiContextId.includes('ITEMS')) {
                            history.push(`${PATH.ITEM_DETAIL}/${taffiContextId}`);
                        } else {
                            history.push(`${PATH.STYLE_DETAIL}/${taffiContextId}`);
                        }
                    } else if (taffiRequestId) {
                        history.push(`${PATH.OLD_SESSION_DETAIL2}/${taffiRequestId}`);
                    }
                }
                // trackEvent(event.data.name, {
                //     sourceUrl: event.data.sourceUrl,
                //     queryParams: event.data.queryParams,
                //     userId: userDetail?.id,
                //     userEmail: userDetail?.email,
                // });
            }
            if (event.data.name === TRACK_EVENT.STYLE_LISTING_PRODUCT_URLS) {
                dispatch(setProductDetails({ productUrls: event.data.productUrls }));
            }

            if (event.data.name === WIDGET_CONSTANTS.SET_SEARCH_WIDGET_GENDER) {
                dispatch(setSearchGender(event.data.searchGender));
            }

            if (event.data.name === WIDGET_CONSTANTS.SEARCH_WIDGET_GET_RECOMMENDATIONS) {
                const gender = getUserGender();
                const input: any = {
                    query: event.data.query,
                    suggested_question_id: event.data.questionId,
                    size: 3,
                    store_id: event.data.storeId,
                    include_existing: true,
                    allowedStyleStatus: store?.allowedStyleStatus,
                };
                if (gender) {
                    input.gender = gender;
                }
                getRecommendations({
                    variables: {
                        input,
                    },
                });
            }

            if (event.data.name === TRACK_EVENT.SEARCH_WIDGET_QUESTION_CLICKED) {
                const sourceUrl = event.data.sourceUrl;
                const queryParams = event.data.queryParams;
                const questionText = event.data.questionText;
                trackEvent(event.data.name, {
                    sourceUrl,
                    queryParams,
                    questionText,
                });
            }

            if (event.data.name === TRACK_EVENT.SEARCH_WIDGET_STYLES_RECOMMENDED) {
                const sourceUrl = event.data.sourceUrl;
                const queryParams = event.data.queryParams;
                const questionText = event.data.questionText;
                const suggestions = event.data.suggestions;
                const styleResults =
                    suggestions?.recommendations?.filter(
                        (recommendation: any) => recommendation.collectionName === 'styles',
                    ) ?? [];
                const numOfStyles = styleResults.length ?? 0;
                const itemResults =
                    suggestions?.recommendations?.filter(
                        (recommendation: any) => recommendation.collectionName === 'items',
                    ) ?? [];
                const numOfItems = itemResults.length ?? 0;
                let styles: any[] = [];
                let items: any[] = [];
                if (styleResults?.length) {
                    styles = styleResults.map((style: any) => {
                        return {
                            taffiId: style.taffiId,
                            styleUrl: `${process.env.REACT_APP_APP_ORIGIN}${PATH.STYLE_DETAIL}/${style?.taffiId}`,
                            sourceId: style.sourceId,
                            sourceTaffiId: style.sourceTaffiId,
                        };
                    });
                }
                if (itemResults?.length) {
                    items = itemResults.map((item: any) => {
                        return {
                            taffiId: item.taffiId,
                            itemUrl: `${process.env.REACT_APP_APP_ORIGIN}${PATH.ITEM_DETAIL}/${item?.taffiId}`,
                        };
                    });
                }
                trackEvent(event.data.name, {
                    sourceUrl,
                    queryParams,
                    questionText,
                    numOfStyles,
                    numOfItems,
                    styles,
                    items,
                    gender: getUserGender(),
                    recommendationSource: STRINGS.STATIC_PRODUCT_SEARCH_PAGE,
                });
            }

            if (event.data.name === TRACK_EVENT.SEARCH_WIDGET_STYLE_CLICKED) {
                const sourceUrl = event.data.sourceUrl;
                const queryParams = event.data.queryParams;
                const questionText = event.data.questionText;
                const style = event.data.style;
                trackEvent(event.data.name, {
                    sourceUrl,
                    queryParams,
                    questionText,
                    recommendationSource: STRINGS.STATIC_PRODUCT_SEARCH_PAGE,
                    style: {
                        taffiId: style.taffiId,
                        styleUrl: `${process.env.REACT_APP_APP_ORIGIN}${PATH.STYLE_DETAIL}/${style.taffiId}`,
                        sourceId: style.sourceId,
                        sourceTaffiId: style.sourceTaffiId,
                    },
                });
            }

            if (event.data.name === TRACK_EVENT.SHOP_THE_PRODUCT) {
                const styleData = event.data.styleData;
                let item = event.data.item;
                if (item.data) {
                    item = item.data;
                }
                trackEvent(event.data.name, {
                    product_type: PRODUCT_TYPE.ITEM,
                    contextType: styleData ? PRODUCT_TYPE.STYLE : '',
                    contextId: styleData?.id,
                    contextTaffiId: styleData?.taffiId,
                    recommendationSource: STRINGS.STATIC_PRODUCT_SEARCH_PAGE,
                    product: pick(item, ['id', 'taffiId', 'title', 'price', 'currency', 'url']),
                });
            }

            if (event.data.name === WIDGET_CONSTANTS.REQUEST_CHECK_PRODUCT_URLS) {
                const productUrls = event.data.productUrls;
                const taffiStoreId = event.data.taffiStoreId;
                // Check if we should use getStylesByUrls instead of getItemsByUrls
                const shouldUseStylesByUrls =
                    store?.enableSingleProductStyling &&
                    store?.allowedStyleStatus?.length === 1 &&
                    store?.allowedStyleStatus?.includes(StyleStatus.published);

                const fetchFunction = shouldUseStylesByUrls ? getStylesByUrls : getItemsByUrls;

                // search for the product urls in Algolia
                fetchFunction(productUrls, shouldUseStylesByUrls ? event?.data?.sourceUrl : taffiStoreId).then(
                    (existedProductUrls) => {
                        window.parent.postMessage(
                            {
                                name: WIDGET_CONSTANTS.RESULT_CHECK_PRODUCT_URLS,
                                existedProductUrls,
                            },
                            '*',
                        );
                    },
                );
            }

            const getLastPath = (url: string) => {
                if (url) {
                    try {
                        const urlObj = new URL(url);
                        const urlPath = urlObj?.pathname;
                        const pathSegments = urlPath?.split('/');
                        const pathLastSegment = pathSegments[pathSegments?.length - 1];
                        return pathLastSegment;
                    } catch (error) {
                        console.error('Invalid URL:', url, error);
                        return '';
                    }
                }
                return '';
            };

            const isAvailableInAlternativeUrls = (item, primaryProductUrl) => {
                let isUrlMatch = false;
                if (item?.data?.alternativeUrls?.length) {
                    item.data.alternativeUrls.forEach((item) => {
                        if (getLastPath(item) === getLastPath(primaryProductUrl ?? '')) {
                            isUrlMatch = true;
                        }
                    });
                }
                return isUrlMatch;
            };

            const getPrimaryProduct = (style, primaryProductUrl) => {
                let primaryProduct = null;
                if (style) {
                    primaryProduct = style.items?.find((item) => {
                        return (
                            getLastPath(item.data?.url) === getLastPath(primaryProductUrl ?? '') ||
                            isAvailableInAlternativeUrls(item, primaryProductUrl)
                        );
                    });
                    return primaryProduct;
                }
                return primaryProduct;
            };

            const handleProductViewEvent = (data, primaryProductUrl) => {
                const primaryProduct: any = getPrimaryProduct(data?.styleData, primaryProductUrl);
                let recommendationSource = STRINGS.STATIC_PRODUCT_DETAIL_PAGE;
                if (data?.isFromPLP) {
                    recommendationSource = STRINGS.STATIC_PRODUCT_LISTING_PAGE;
                }
                if (data?.isFromSearch) {
                    recommendationSource = STRINGS.STATIC_PRODUCT_SEARCH_PAGE;
                }
                const styleData = data.styleData;
                const productViewTrackingData = {
                    ...getProductTrackingData(userDetail, {
                        ...data.styleData,
                        collection: styleData?.collection ?? styleData?.collectionName ?? 'styles',
                    }),
                    styleLayoutVersion: data?.styleLayoutVersion,
                    personalized: checkIsPersonalizedStyle(data.styleData, userDetail),
                    recommendationSource,
                    sourceUrl: data.sourceUrl,
                    queryParams: data.queryParams,
                    primaryStoreProductId: primaryProduct?.data?.storeProductId,
                    primaryTaffiProductId: primaryProduct?.data?.taffiId,
                    primaryProductId: primaryProduct?.id,
                };
                trackEvent(TRACK_EVENT.PRODUCT_VIEW, productViewTrackingData);
            };

            const handleHangerClickAnalyticsEvent = (data) => {
                trackEvent(TRACK_EVENT.TAFFI_PLP_WIDGET_ICON_CLICK, data);
            };

            if (event.data.name === WIDGET_CONSTANTS.TAFFI_PLP_WIDGET_ICON_CLICK) {
                handleHangerClickAnalyticsEvent({
                    sourceUrl: event.data.sourceUrl,
                    productUrl: event.data.productUrl,
                    queryParams: event.data.queryParams,
                    styleId: event.data.styleId,
                    styleTaffiId: event.data.styleTaffiId,
                    targetState: event.data.targetState,
                });
            }
            if (event.data.name === TRACK_EVENT.CHECK_PENDING_TAFFI_PLP_HANGER_ICON_DISPLAYED) {
                if (hangerIconDisplayedDataRef?.current?.length) {
                    trackEvent(TRACK_EVENT.TAFFI_PLP_HANGER_ICON_DISPLAYED, {
                        products: [...hangerIconDisplayedDataRef?.current],
                        productCount: hangerIconDisplayedDataRef?.current?.length,
                        recommendationSource: STRINGS.STATIC_PRODUCT_LISTING_PAGE,
                        sourceUrl: event.data.sourceUrl,
                        queryParams: event.data.queryParams,
                        userId: userDetail?.id || localUser?.id,
                    });
                    hangerIconDisplayedDataRef.current = [];
                }
            }
            if (event.data.name === TRACK_EVENT.TAFFI_PLP_HANGER_ICON_DISPLAYED) {
                const sourceUrl = event.data.sourceUrl;
                const productUrl = event.data.productUrl;
                const baseUrlObject = new URL(sourceUrl);
                const itemUrl = baseUrlObject.origin + productUrl;
                const cacheKey = itemUrl;
                if (stylesCache.current.has(cacheKey)) {
                    const cachedStyles = stylesCache.current.get(cacheKey);
                    const styleData = cachedStyles[0];
                    const primaryProduct: any = getPrimaryProduct(styleData, itemUrl);
                    const itemTrackingData = getItemTrackingData(primaryProduct?.data);
                    hangerIconDisplayedDataRef.current.push(itemTrackingData);
                }
            }
            if (event.data.name === WIDGET_CONSTANTS.REQUEST_STYLING_PRODUCT) {
                try {
                    const sourceUrl = event.data.sourceUrl;
                    const productUrl = event.data.productUrl;
                    const taffiStoreId = event.data.taffiStoreId;
                    const queryParams = event.data.queryParams;
                    const baseUrlObject = new URL(sourceUrl);
                    const itemUrl = baseUrlObject.origin + productUrl;
                    const cacheKey = itemUrl;

                    // Check if styles are available in cache
                    if (stylesCache.current.has(cacheKey)) {
                        const cachedStyles = stylesCache.current.get(cacheKey);
                        const styleData = cachedStyles[0];
                        handleHangerClickAnalyticsEvent({
                            sourceUrl,
                            productUrl: itemUrl,
                            queryParams,
                            targetState: 'style',
                            styleId: styleData?.id,
                            styleTaffiId: styleData?.taffiId,
                        });
                        handleProductViewEvent(
                            {
                                isFromPLP: true,
                                styleData,
                                sourceUrl,
                                queryParams,
                            },
                            itemUrl,
                        );
                        window.parent.postMessage(
                            {
                                name: WIDGET_CONSTANTS.RESULT_STYLING_PRODUCT,
                                styles: cachedStyles,
                                productUrl,
                            },
                            '*',
                        );
                        return; // Early return to avoid getSingleRecommendation call
                    }

                    // If not in cache, proceed with original flow
                    const input: any = {
                        item_url: itemUrl,
                        size: 1,
                        store_id: store?.id || taffiStoreId,
                        include_existing: true,
                        allowedStyleStatus: store?.allowedStyleStatus,
                    };
                    getSingleRecommendation({ variables: { input } })
                        .then(async (response: any) => {
                            const styles = response?.data?.getRecommendations?.recommendations;
                            if (styles?.length) {
                                const promises: any[] = [];
                                for (const style of styles) {
                                    const itemCondition = style.items.reduce((condition: string, i: any) => {
                                        condition += (condition === '' ? '' : ' OR ') + `objectID:${i.id}`;
                                        return condition;
                                    }, '');
                                    const itemsResponse = await getItem({
                                        variables: {
                                            filters: itemCondition,
                                        },
                                    });
                                    const items = itemsResponse.data?.algolia_get_item_by_id.hits.map((data: any) => {
                                        return {
                                            id: data.id,
                                            data,
                                        };
                                    });
                                    promises.push({
                                        ...style,
                                        items: items
                                            ? styleItemUtils.mergeItemArrayObjects(style.items, items)
                                            : style.items,
                                    });
                                }
                                const result = await Promise.all(promises);
                                const styleData = result[0];
                                handleHangerClickAnalyticsEvent({
                                    sourceUrl,
                                    productUrl: itemUrl,
                                    queryParams,
                                    styleId: styleData?.id,
                                    styleTaffiId: styleData?.taffiId,
                                    targetState: 'style',
                                });
                                handleProductViewEvent(
                                    {
                                        isFromPLP: true,
                                        styleData,
                                        sourceUrl,
                                        queryParams,
                                    },
                                    itemUrl,
                                );
                                window.parent.postMessage(
                                    {
                                        name: WIDGET_CONSTANTS.RESULT_STYLING_PRODUCT,
                                        styles: result,
                                        productUrl,
                                    },
                                    '*',
                                );
                            } else {
                                handleHangerClickAnalyticsEvent({
                                    sourceUrl,
                                    productUrl: itemUrl,
                                    queryParams,
                                    styleId: null,
                                    styleTaffiId: null,
                                    targetState: 'style',
                                });
                                window.parent.postMessage(
                                    {
                                        name: WIDGET_CONSTANTS.RESULT_STYLING_PRODUCT,
                                        styles: null,
                                        productUrl,
                                    },
                                    '*',
                                );
                            }
                        })
                        .catch((e) => {
                            console.log('Error styling product', event.data, e);
                            window.parent.postMessage(
                                {
                                    name: WIDGET_CONSTANTS.RESULT_STYLING_PRODUCT,
                                    styles: null,
                                    productUrl,
                                },
                                '*',
                            );
                        });
                } catch (e) {
                    console.log('Error styling product', event.data, e);
                    window.parent.postMessage(
                        {
                            name: WIDGET_CONSTANTS.RESULT_STYLING_PRODUCT,
                            style: null,
                            productUrl: event.data.productUrl,
                        },
                        '*',
                    );
                }
            }
        },
        [
            userDetail,
            history,
            dispatch,
            location.pathname,
            getUserGender,
            store?.allowedStyleStatus,
            store?.enableSingleProductStyling,
            store?.id,
            getRecommendations,
            getItemsByUrls,
            getStylesByUrls,
            getSingleRecommendation,
            getItem,
            stylesCache,
            localUser?.id,
        ],
    );

    window.onbeforeunload = function () {
        if (isInternalLinkClicked) {
            dispatch(updateIsLinkClicked(false));
        } else {
            sessionStorage.setItem(WIDGET_CONSTANTS.TAFFI_WIDGET_OPENED, JSON.stringify(false));
            dispatch(setWidgetOpenState(false));
        }
    };

    useEffect(() => {
        // Add a listener for the 'message' event on the window object
        window.addEventListener('message', handleMessage);
        //send event to parent page to tell that widget is ready to handle events
        window.parent.postMessage({ name: WIDGET_CONSTANTS.WIDGET_READY_TO_HANDLE_EVENTS }, '*');
        // Clean up the listener when the component unmounts
        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [handleMessage]);

    return (
        <>
            <div
                id={'taffi-main'}
                style={{
                    // height: `${height}px`,
                    height: '100%',
                    maxWidth: `1024px`,
                    margin: 'auto',
                    position: 'fixed',
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0,
                }}
            >
                <ScrollToTop>
                    <MainRouting />
                </ScrollToTop>
                <AuthWidget />
                <NotificationPerTime />
                <UserDetailProvider />
                <AppVersionProvider />
                <LogConfig />
                <GlobalLoadingSpinner />
                <BottomNavigationProvider />
                <AppInternetStatusProvider />
                <div id="over_bottom_bar" />
            </div>
            <ToastContainer />
        </>
    );
};

export default App;
